// SeccionComestible — P3-5b. La sección COMESTIBLE de la ficha: se muestra cuando el
// tipo de producto tiene arquetipo «consumible». Captura todos los campos del contrato
// (subtítulo, ejes sabor/pack con foto/hex/descriptor/sello, variaciones, tabla
// nutricional, sellos 810/2021, cómo se toma, beneficios, FAQ, beneficios propios con
// imagen, tarjeta de confianza, video+portada). El Guardar de la ficha traduce con
// comestibleAModelo y lo manda al motor (backend/consumibles/). Los errores del motor
// (por campo) llegan en `errores` y se listan arriba.
import React, { useState } from 'react';
import { Plus, Trash2, Image as ImageIcon } from 'lucide-react';
import ProductImageSelector from './ProductImageSelector';
import { SELLOS_810, claveVariacion } from '../utils/comestibleDraft';
import './SeccionComestible.css';

// Campo de UNA imagen (id de la mediateca): botón que abre el selector y muestra miniatura.
function CampoImagen({ valor, src, onPick, nombre = '', etiqueta = 'Imagen' }) {
  const [abierto, setAbierto] = useState(false);
  return (
    <div className="com-img">
      {valor ? (
        <img className="com-img-mini" src={src || ''} alt="" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
      ) : <div className="com-img-ph"><ImageIcon size={16} /></div>}
      <div className="com-img-acc">
        <button type="button" className="com-mini-btn" onClick={() => setAbierto(true)}>{valor ? 'Cambiar' : etiqueta}</button>
        {valor ? <button type="button" className="com-mini-btn" onClick={() => onPick(0, '')}>Quitar</button> : null}
      </div>
      {abierto && (
        <ProductImageSelector open nombre={nombre} onClose={() => setAbierto(false)}
          onSelect={(imgs) => { const im = (imgs || [])[0]; if (im) onPick(im.id, im.src); setAbierto(false); }} />
      )}
    </div>
  );
}

function Lista({ titulo, children, onAgregar, textoAgregar, id }) {
  return (
    <div className="com-bloque" id={id}>
      <div className="com-bloque-top"><h4>{titulo}</h4>
        <button type="button" className="com-mini-btn" onClick={onAgregar}><Plus size={13} /> {textoAgregar}</button>
      </div>
      {children}
    </div>
  );
}

const irAlCampo = (ancla) => {
  if (!ancla) return;
  const el = typeof document !== 'undefined' && document.getElementById(ancla);
  if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

export default function SeccionComestible({ comestible: c, onChange, errores = [], aviso = null, nombre = '' }) {
  const set = (parche) => onChange({ ...c, ...parche });
  const setArr = (campo, i, parche) => set({ [campo]: c[campo].map((x, j) => (j === i ? { ...x, ...parche } : x)) });
  const quitarArr = (campo, i) => set({ [campo]: c[campo].filter((_, j) => j !== i) });
  const nut = c.nutricion || { porcion: '', porciones: '', filas: [] };
  const setNut = (parche) => set({ nutricion: { ...nut, ...parche } });
  const setVar = (sabor, pack, parche) => {
    const k = claveVariacion(sabor, pack);
    set({ variaciones: { ...c.variaciones, [k]: { ...(c.variaciones[k] || {}), ...parche } } });
  };
  const cf = c.confianza || {};

  return (
    <div className="com-seccion">
      {aviso && <div className="com-aviso" role="alert">{aviso}</div>}
      {errores.length > 0 && (
        <div className="com-errores" role="alert">
          <strong>Revisa estos campos:</strong>
          <ul>{errores.map((e, i) => (
            <li key={i}>
              {e.ancla
                ? <button type="button" className="com-err-link" onClick={() => irAlCampo(e.ancla)}>{e.campo}</button>
                : <span className="com-err-campo">{e.campo}</span>}
              {' — '}{e.mensaje}
            </li>
          ))}</ul>
        </div>
      )}

      <label className="com-campo" id="com-subtitulo">Subtítulo
        <input type="text" value={c.subtitulo} onChange={(e) => set({ subtitulo: e.target.value })} placeholder="Proteína vegetal · 20 g" />
      </label>

      {/* ── Ejes ── */}
      <Lista titulo="Sabores" id="com-sabores" textoAgregar="Agregar sabor" onAgregar={() => set({ sabores: [...c.sabores, { nombre: '', descriptor: '', hex: '', thumbnailId: 0, thumbnailSrc: '' }] })}>
        {c.sabores.map((s, i) => (
          <div className="com-fila com-fila--sabor" key={i}>
            <input placeholder="Nombre" value={s.nombre} onChange={(e) => setArr('sabores', i, { nombre: e.target.value })} />
            <input placeholder="Descriptor" value={s.descriptor} onChange={(e) => setArr('sabores', i, { descriptor: e.target.value })} />
            <input type="color" title="Color del sabor" value={s.hex || '#cccccc'} onChange={(e) => setArr('sabores', i, { hex: e.target.value })} />
            <CampoImagen valor={s.thumbnailId} src={s.thumbnailSrc} nombre={`${nombre} ${s.nombre}`} etiqueta="Foto"
              onPick={(id, src) => setArr('sabores', i, { thumbnailId: id, thumbnailSrc: src })} />
            <button type="button" className="com-mini-btn com-mini-btn--danger" onClick={() => quitarArr('sabores', i)}><Trash2 size={13} /></button>
          </div>
        ))}
      </Lista>

      <Lista titulo="Presentaciones (packs)" id="com-packs" textoAgregar="Agregar pack" onAgregar={() => set({ packs: [...c.packs, { nombre: '', sublabel: '', sello: '' }] })}>
        {c.packs.length === 0 && (
          <p className="com-vacio-hint">Agrega al menos un pack; con sabores y packs se generan las variaciones.</p>
        )}
        {c.packs.map((p, i) => (
          <div className="com-fila com-fila--pack" key={i}>
            <input placeholder="Nombre (Unidad, Caja × 6)" value={p.nombre} onChange={(e) => setArr('packs', i, { nombre: e.target.value })} />
            <input placeholder="Sublabel (6 barras)" value={p.sublabel} onChange={(e) => setArr('packs', i, { sublabel: e.target.value })} />
            <input placeholder="Sello (Ahorra 10%)" value={p.sello} onChange={(e) => setArr('packs', i, { sello: e.target.value })} />
            <button type="button" className="com-mini-btn com-mini-btn--danger" onClick={() => quitarArr('packs', i)}><Trash2 size={13} /></button>
          </div>
        ))}
      </Lista>

      {/* ── Variaciones (sabor × pack) ── */}
      {c.sabores.some((s) => s.nombre) && c.packs.some((p) => p.nombre) && (
        <div className="com-bloque" id="com-variaciones">
          <div className="com-bloque-top"><h4>Precios y stock por variación</h4></div>
          <div className="com-var-grid">
            {c.sabores.filter((s) => s.nombre).map((s) => c.packs.filter((p) => p.nombre).map((p) => {
              const v = c.variaciones[claveVariacion(s.nombre, p.nombre)] || {};
              return (
                <div className="com-var-cell" key={`${s.nombre}-${p.nombre}`}>
                  <span className="com-var-label">{s.nombre} · {p.nombre}</span>
                  <input type="number" placeholder="Precio" value={v.precio || ''} onChange={(e) => setVar(s.nombre, p.nombre, { precio: e.target.value })} />
                  <input type="number" placeholder="Stock" value={v.stock ?? ''} onChange={(e) => setVar(s.nombre, p.nombre, { stock: e.target.value })} />
                  <CampoImagen valor={v.imagenId} src={v.imagenSrc} nombre={`${s.nombre} ${p.nombre}`} etiqueta="Foto"
                    onPick={(id, src) => setVar(s.nombre, p.nombre, { imagenId: id, imagenSrc: src })} />
                </div>
              );
            }))}
          </div>
        </div>
      )}

      {/* ── Tabla nutricional ── */}
      <div className="com-bloque" id="com-nutricion">
        <div className="com-bloque-top"><h4>Tabla nutricional</h4>
          <button type="button" className="com-mini-btn" onClick={() => setNut({ filas: [...nut.filas, { nutriente: '', cantidad: '', unidad: '', vd: '' }] })}><Plus size={13} /> Fila</button>
        </div>
        <div className="com-fila">
          <input placeholder="Porción (1 barra, 60 g)" value={nut.porcion} onChange={(e) => setNut({ porcion: e.target.value })} />
          <input placeholder="Porciones por envase (12)" value={nut.porciones} onChange={(e) => setNut({ porciones: e.target.value })} />
        </div>
        {nut.filas.map((f, i) => (
          <div className="com-fila com-fila--nut" key={i}>
            <input placeholder="Nutriente" value={f.nutriente} onChange={(e) => setNut({ filas: nut.filas.map((x, j) => (j === i ? { ...x, nutriente: e.target.value } : x)) })} />
            <input placeholder="Cantidad" value={f.cantidad} onChange={(e) => setNut({ filas: nut.filas.map((x, j) => (j === i ? { ...x, cantidad: e.target.value } : x)) })} />
            <input placeholder="Unidad (g)" value={f.unidad} onChange={(e) => setNut({ filas: nut.filas.map((x, j) => (j === i ? { ...x, unidad: e.target.value } : x)) })} />
            <input placeholder="%VD (solo número)" value={f.vd} onChange={(e) => setNut({ filas: nut.filas.map((x, j) => (j === i ? { ...x, vd: e.target.value } : x)) })} />
            <button type="button" className="com-mini-btn com-mini-btn--danger" onClick={() => setNut({ filas: nut.filas.filter((_, j) => j !== i) })}><Trash2 size={13} /></button>
          </div>
        ))}
      </div>

      <label className="com-campo">Ingredientes
        <textarea rows={3} value={c.ingredientes} onChange={(e) => set({ ingredientes: e.target.value })} placeholder="Un ingrediente por línea o separados por coma" />
      </label>
      <label className="com-campo">Alérgenos
        <textarea rows={2} value={c.alergenos} onChange={(e) => set({ alergenos: e.target.value })} />
      </label>
      <label className="com-campo">Cómo se toma
        <textarea rows={2} value={c.comoUsar} onChange={(e) => set({ comoUsar: e.target.value })} />
      </label>

      {/* ── Sellos 810/2021 ── */}
      <div className="com-bloque">
        <div className="com-bloque-top"><h4>Sellos (Ley 810 / 2021)</h4></div>
        <div className="com-sellos">
          {SELLOS_810.map((s) => (
            <label key={s.id} className="com-check">
              <input type="checkbox" checked={c.sellos.includes(s.id)}
                onChange={(e) => set({ sellos: e.target.checked ? [...c.sellos, s.id] : c.sellos.filter((x) => x !== s.id) })} />
              {s.label}
            </label>
          ))}
        </div>
      </div>

      <label className="com-campo" id="com-beneficios">Beneficios (texto con formato simple)
        <textarea rows={3} value={c.beneficiosDetalle} onChange={(e) => set({ beneficiosDetalle: e.target.value })} placeholder="<ul><li>…</li></ul> o párrafos" />
      </label>

      {/* ── FAQ propia ── */}
      <Lista titulo="Preguntas frecuentes (propias)" textoAgregar="Agregar pregunta" onAgregar={() => set({ faq: [...c.faq, { pregunta: '', respuesta: '' }] })}>
        {c.faq.map((x, i) => (
          <div className="com-fila com-fila--faq" key={i}>
            <input placeholder="Pregunta" value={x.pregunta} onChange={(e) => setArr('faq', i, { pregunta: e.target.value })} />
            <input placeholder="Respuesta" value={x.respuesta} onChange={(e) => setArr('faq', i, { respuesta: e.target.value })} />
            <button type="button" className="com-mini-btn com-mini-btn--danger" onClick={() => quitarArr('faq', i)}><Trash2 size={13} /></button>
          </div>
        ))}
      </Lista>

      {/* ── Beneficios propios con imagen ── */}
      <Lista titulo="Beneficios con imagen" textoAgregar="Agregar beneficio" onAgregar={() => set({ beneficios: [...c.beneficios, { imagen: 0, imagenSrc: '', titulo: '', texto: '' }] })}>
        {c.beneficios.map((b, i) => (
          <div className="com-fila com-fila--benef" key={i}>
            <CampoImagen valor={b.imagen} src={b.imagenSrc} nombre={b.titulo} etiqueta="Imagen"
              onPick={(id, src) => setArr('beneficios', i, { imagen: id, imagenSrc: src })} />
            <input placeholder="Título" value={b.titulo} onChange={(e) => setArr('beneficios', i, { titulo: e.target.value })} />
            <input placeholder="Texto" value={b.texto} onChange={(e) => setArr('beneficios', i, { texto: e.target.value })} />
            <button type="button" className="com-mini-btn com-mini-btn--danger" onClick={() => quitarArr('beneficios', i)}><Trash2 size={13} /></button>
          </div>
        ))}
      </Lista>

      {/* ── Tarjeta de confianza ── */}
      <div className="com-bloque">
        <div className="com-bloque-top"><h4>Tarjeta de confianza</h4></div>
        <div className="com-fila">
          <input placeholder="Ícono (verificado)" value={cf.icono || ''} onChange={(e) => set({ confianza: { ...cf, icono: e.target.value } })} />
          <input placeholder="Título" value={cf.titulo || ''} onChange={(e) => set({ confianza: { ...cf, titulo: e.target.value } })} />
          <input placeholder="Fecha (2026)" value={cf.fecha || ''} onChange={(e) => set({ confianza: { ...cf, fecha: e.target.value } })} />
          <CampoImagen valor={cf.imagen} src={cf.imagenSrc} etiqueta="Imagen"
            onPick={(id, src) => set({ confianza: { ...cf, imagen: id, imagenSrc: src } })} />
        </div>
        <textarea rows={2} placeholder="Texto" value={cf.texto || ''} onChange={(e) => set({ confianza: { ...cf, texto: e.target.value } })} />
        <div className="com-fila">
          <input placeholder="Texto del enlace" value={cf.enlace_texto || ''} onChange={(e) => set({ confianza: { ...cf, enlace_texto: e.target.value } })} />
          <input placeholder="URL del enlace" value={cf.enlace_url || ''} onChange={(e) => set({ confianza: { ...cf, enlace_url: e.target.value } })} />
        </div>
      </div>

      {/* ── Video + portada ── */}
      <div className="com-bloque">
        <div className="com-bloque-top"><h4>Video</h4></div>
        <div className="com-fila">
          <input placeholder="URL del video (https://…)" value={c.video} onChange={(e) => set({ video: e.target.value })} />
          <CampoImagen valor={c.videoPortada} src={c.videoPortadaSrc} etiqueta="Portada"
            onPick={(id, src) => set({ videoPortada: id, videoPortadaSrc: src })} />
        </div>
      </div>
    </div>
  );
}
